import React, { useEffect } from 'react';
import {
  Box, Typography, Stack, TextField, InputAdornment, Select, MenuItem, Switch,
} from '@mui/material';
import {
  Flame, MoonStar, Moon, Sun, Sunset, Sunrise, Star, Sparkles,
  Wine, Wheat, TreePalm, Droplets, Clock, Bell, Heart, CalendarDays, BookOpen, Zap,
} from 'lucide-react';
import { ShabbatCandles } from '../../../business/widgetIconMap';

const navy = '#1e3a5f';
const fieldSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

const TZEIT_OPTIONS = [
  { value: 'tzeit85deg',  label: 'צאת כוכבים 8.5°  (מרבית הפוסקים)' },
  { value: 'tzeit7083deg', label: 'צאת כוכבים 7.083° (מחמיר)' },
  { value: 'tzeit42min',  label: "צאת 42 דקות" },
  { value: 'tzeit50min',  label: "צאת 50 דקות" },
  { value: 'tzeit72min',  label: "צאת 72 דקות (ר\"ת)" },
];

const ICON_OPTIONS = [
  { value: 'ShabbatCandles', label: 'נרות שבת', Icon: ShabbatCandles },
  { value: 'Flame',          label: 'נר',        Icon: Flame         },
  { value: 'Sunset',         label: 'שקיעה',     Icon: Sunset        },
  { value: 'MoonStar',       label: 'כוכבים',    Icon: MoonStar      },
  { value: 'Moon',           label: 'ירח',       Icon: Moon          },
  { value: 'Sun',            label: 'שמש',       Icon: Sun           },
  { value: 'Sunrise',        label: 'זריחה',     Icon: Sunrise       },
  { value: 'Star',           label: 'כוכב',      Icon: Star          },
  { value: 'Sparkles',       label: 'ניצוץ',     Icon: Sparkles      },
  { value: 'Wine',           label: 'יין',       Icon: Wine          },
  { value: 'Wheat',          label: 'חיטה',      Icon: Wheat         },
  { value: 'TreePalm',       label: 'דקל',       Icon: TreePalm      },
  { value: 'Droplets',       label: 'מים',       Icon: Droplets      },
  { value: 'Clock',          label: 'שעון',      Icon: Clock         },
  { value: 'Bell',           label: 'פעמון',     Icon: Bell          },
  { value: 'Heart',          label: 'לב',        Icon: Heart         },
  { value: 'CalendarDays',   label: 'לוח',       Icon: CalendarDays  },
  { value: 'BookOpen',       label: 'ספר',       Icon: BookOpen      },
  { value: 'Zap',            label: 'ברק',       Icon: Zap           },
];

const DEFAULT_ENTRY = { key: 'sunset', minsBeforeSunset: 18, offset: 0, icon: 'ShabbatCandles' };
const DEFAULT_EXIT  = { key: 'tzeit85deg', offset: 0, icon: 'MoonStar' };

const selectSx = {
  fontSize: '0.8rem', bgcolor: 'white',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
};

const IconSelect = ({ value, onChange }) => {
  return (
    <Select size="small" value={value} onChange={(e) => onChange(e.target.value)}
      renderValue={(v) => {
        const opt = ICON_OPTIONS.find(o => o.value === v);
        if (!opt) return v;
        return (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <opt.Icon size={15} color="#374151" />
            <Typography sx={{ fontSize: '0.8rem' }}>{opt.label}</Typography>
          </Stack>
        );
      }}
      sx={{ ...selectSx, width: 130 }}
      MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
    >
      {ICON_OPTIONS.map(opt => (
        <MenuItem key={opt.value} value={opt.value} sx={{ py: 0.75 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <opt.Icon size={16} color="#374151" />
            <Typography sx={{ fontSize: '0.82rem' }}>{opt.label}</Typography>
          </Stack>
        </MenuItem>
      ))}
    </Select>
  );
};

/**
 * ShabbatCalcStep
 * הגדרת שיטת חישוב כניסת ויציאת שבת — כמה דקות לפני השקיעה (או פלג המנחה) לכניסה,
 * וסוג צאת כוכבים ליציאה. זמנים אלה ישמשו כ"כניסת שבת" ו"יציאת שבת" בתפריטי בחירת זמן תפילה.
 */
const ShabbatCalcStep = ({ formData, onChange }) => {
  const entry = { ...DEFAULT_ENTRY, ...formData?.entryZman };
  const exit  = { ...DEFAULT_EXIT,  ...formData?.exitZman  };
  const showEntryExit = formData?.showEntryExit ?? true;

  useEffect(() => {
    if (!formData?.entryZman || !formData?.exitZman) {
      onChange({
        entryZman: formData?.entryZman || { ...DEFAULT_ENTRY },
        exitZman:  formData?.exitZman  || { ...DEFAULT_EXIT  },
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setEntry = (partial) => onChange({ entryZman: { ...entry, ...partial } });
  const setExit  = (partial) => onChange({ exitZman:  { ...exit,  ...partial } });

  const handleMins = (e) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-') { setEntry({ minsBeforeSunset: raw }); return; }
    const n = parseInt(raw, 10);
    if (!isNaN(n) && n >= 0) setEntry({ minsBeforeSunset: n });
  };

  const handlePlagOffset = (e) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-') { setEntry({ offset: raw }); return; }
    const n = parseInt(raw, 10);
    if (!isNaN(n)) setEntry({ offset: n });
  };

  const handleExitOffset = (e) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-') { setExit({ offset: raw }); return; }
    const n = parseInt(raw, 10);
    if (!isNaN(n)) setExit({ offset: n });
  };

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        הגדרת זמני כניסה ויציאת שבת
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2.5, lineHeight: 1.65 }}>
        קבע כמה דקות לפני השקיעה מדליקים נרות בקהילתך ומתי שבת יוצאת.
        זמנים אלה ישמשו כ<strong>כניסת שבת</strong> ו<strong>יציאת שבת</strong> בתפריט הגדרת שעות תפילה.
      </Typography>

      {/* הצגת זמני כניסה ויציאה */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2, mb: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography sx={{ fontSize: '0.875rem', color: '#111827', fontWeight: 500 }}>
              הצג כניסת ויציאת שבת
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>
              הצגת שעות כניסה ויציאה בתבנית
            </Typography>
          </Box>
          <Switch
            checked={showEntryExit}
            onChange={(e) => onChange({ showEntryExit: e.target.checked })}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: navy },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: navy },
            }}
          />
        </Stack>
      </Box>

      {/* ── כניסה ─── */}
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '0.9rem', mb: 1 }}>כניסת שבת</Typography>
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 2.5 }}>
        {entry.key === 'sunset' ? (
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" gap={1}>
              <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>
                כמה דקות לפני השקיעה?
              </Typography>
              <TextField
                size="small"
                value={entry.minsBeforeSunset}
                onChange={handleMins}
                inputProps={{ style: { textAlign: 'center', fontSize: '1.1rem', fontWeight: 700, width: 36 } }}
                InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>דקות</Typography></InputAdornment> }}
                sx={{ width: 100, ...fieldSx }}
              />
              <Typography sx={{ fontSize: '0.78rem', color: '#9ca3af' }}>(מקובל 18–40 דקות)</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 500 }}>אייקון:</Typography>
              <IconSelect value={entry.icon || 'ShabbatCandles'} onChange={(v) => setEntry({ icon: v })} />
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography onClick={() => setEntry({ key: 'plagHamincha', offset: 0 })}
                sx={{ fontSize: '0.73rem', color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}>
                השתמש בפלג המנחה במקום →
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>פלג המנחה</Typography>
              <TextField size="small" value={entry.offset ?? 0} onChange={handlePlagOffset}
                inputProps={{ style: { textAlign: 'center', fontSize: '0.85rem', width: 28 } }}
                InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>דק׳</Typography></InputAdornment> }}
                sx={{ width: 80, ...fieldSx }}
              />
              <Box sx={{ flex: 1 }} />
              <Typography onClick={() => setEntry({ key: 'sunset', minsBeforeSunset: 18 })}
                sx={{ fontSize: '0.73rem', color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}>
                ← חזור לשקיעה
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 500 }}>אייקון:</Typography>
              <IconSelect value={entry.icon || 'ShabbatCandles'} onChange={(v) => setEntry({ icon: v })} />
            </Stack>
          </Stack>
        )}
      </Box>

      {/* ── יציאה ─── */}
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '0.9rem', mb: 1 }}>יציאת שבת</Typography>
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" gap={1}>
            <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>צאת הכוכבים:</Typography>
            <Select size="small" value={exit.key || 'tzeit85deg'}
              onChange={(e) => setExit({ key: e.target.value })}
              sx={{ ...selectSx, minWidth: 240 }}>
              {TZEIT_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.82rem' }}>{opt.label}</MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 500 }}>הוסף דקות:</Typography>
            <TextField size="small" value={exit.offset ?? 0} onChange={handleExitOffset}
              inputProps={{ style: { textAlign: 'center', fontSize: '0.85rem', width: 28 } }}
              InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>דק׳</Typography></InputAdornment> }}
              sx={{ width: 80, ...fieldSx }}
            />
            <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>(-/+ לפני/אחרי)</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 500 }}>אייקון:</Typography>
            <IconSelect value={exit.icon || 'MoonStar'} onChange={(v) => setExit({ icon: v })} />
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default ShabbatCalcStep;
